import { Component, ViewChild, inject, signal, computed, OnInit, ChangeDetectionStrategy } from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { CommonModule } from '@angular/common';
import { FormBuilder, FormGroup, FormsModule, Validators } from '@angular/forms';
import { NgbModal, NgbModalRef } from '@ng-bootstrap/ng-bootstrap';
import { CatalogService } from '@services/data/catalog.service';
import { FormMode } from 'app/core/constants/form-mode';
import Swal from 'sweetalert2';

import { PermissionService } from '@services/core/permission.service';
import { AppSettings } from '@services/settings/app-settings.service';
import { PageHeaderComponent } from 'app/core/components/page-header/page-header.component';
import { MasterDetailNav } from 'app/core/service/utils/master-detail-nav.service';
import { ListCardComponent } from 'app/core/components/list-card/list-card.component';
import { MobileFooterComponent } from 'app/core/components/mobile-footer/mobile-footer.component';
import { RoleService } from '../../services/role.service';
import { RoleFormComponent } from '../../components/role-form/role-form.component';
import { RoleMenusListComponent } from '../../components/role-menus-list/role-menus-list.component';
import { RoleMenusFormComponent } from '../../components/role-menus-form/role-menus-form.component';
import { NgSelectModule } from '@ng-select/ng-select';
import {
    expandMenuIdsWithParents,
    flattenMenusAsTree,
    menuKey,
    mergeMenuCatalog
} from '../../utils/menu-tree.util';

@Component({
    selector: 'app-roles-management',
    standalone: true,
    imports: [
        CommonModule,
        FormsModule,
        RoleFormComponent,
        RoleMenusListComponent,
        RoleMenusFormComponent,
        NgSelectModule,
        PageHeaderComponent,
        ListCardComponent,
        MobileFooterComponent
    ],
    changeDetection: ChangeDetectionStrategy.Eager,
    templateUrl: './roles-management.component.html'
})
export class RolesManagementComponent implements OnInit {

    @ViewChild('menuModal') menuModal!: any;
    @ViewChild('newRole') newRole!: any;

    selectedRole: any = null;
    roleForm: FormGroup;
    mode = signal<FormMode>(FormMode.VIEW);
    formMode = FormMode;
    menusEditing = signal(false);
    private menusSnapshot: any[] = [];
    private modalRef?: NgbModalRef;
    private loadedRoleKey?: string;

    private fb = inject(FormBuilder);
    private roleService = inject(RoleService);
    public catalogService = inject(CatalogService);
    readonly perm = inject(PermissionService);
    public appSettings = inject(AppSettings);
    private md = inject(MasterDetailNav);
    private readonly basePath = 'seguridad/roles';
    roleSearch = '';

    ngOnInit(): void {
        this.catalogService.reloadMenus();
    }

    loadRoles() {
        this.catalogService.reloadRoles();
    }

    constructor(private modalService: NgbModal) {
        this.roleForm = this.fb.group({
            id: [null],
            slug: [null],
            name: [null, [Validators.required]],
            displayName: [null, [Validators.required]],
            description: [null],
            level: [2, [Validators.required]],
            category: [null],
            isActive: [true]
        });
        this.md.urlChanges$.pipe(takeUntilDestroyed()).subscribe(() => this.syncFromRoute());
    }

    open(content, size: 'sm' | 'md' | 'lg' | 'xl' = 'lg') {
        this.modalRef = this.modalService.open(content, { size: size });
    }

    close() {
        if (this.modalRef) {
            this.modalRef.close();
        }
    }

    onRoleSelected(role: any) {
        if (!role) {
            this.selectedRole = null;
            this.loadedRoleKey = undefined;
            this.roleForm.reset();
            this.md.go(this.md.list(this.basePath));
            return;
        }
        this.applyRole(role);
        if (!role.isNew) {
            const key = String(role.slug ?? role.name ?? role.id);
            if (this.md.detailSegment(this.basePath) !== key) {
                this.md.go(this.md.item(this.basePath, key));
            }
        }
    }

    private applyRole(role: any) {
        if (!role) return;
        const key = role.slug ?? role.name ?? role.id;
        this.loadedRoleKey = String(key);
        if (key && !role.isNew) {
            this.roleService.getById(key).subscribe({
                next: (res: any) => this._applyRoleMenusResponse(res),
                error: () => this._fallbackRole(role)
            });
            return;
        }
        this._fallbackRole(role);
    }

    private _fallbackRole(role: any) {
        this._stopMenusEdit();
        this.selectedRole = { ...role, menus: this._normalizeMenus(role.menus) };
        this.mode.set(FormMode.VIEW);
        this.roleForm.reset();
        this.roleForm.patchValue(this.selectedRole);
    }

    private syncFromRoute(retries = 12) {
        const id = this.md.detailSegment(this.basePath);
        if (!id) {
            this.selectedRole = null;
            this.loadedRoleKey = undefined;
            this._stopMenusEdit();
            this.mode.set(FormMode.VIEW);
            return;
        }
        if (this.loadedRoleKey === id) return;
        const found = (this.catalogService.roles() || []).find((r: any) =>
            String(r.slug ?? r.name ?? r.id) === id
        );
        if (found) {
            this.applyRole(found);
            return;
        }
        if (retries > 0) setTimeout(() => this.syncFromRoute(retries - 1), 80);
    }

    get showRoleDetail(): boolean {
        return !!this.selectedRole && !this.selectedRole.isNew;
    }

    filteredCatalogRoles(): any[] {
        const term = this.roleSearch.trim().toLowerCase();
        const list = this.catalogService.roles() || [];
        if (!term) return list;
        return list.filter((r: any) =>
            [r.displayName, r.description, r.slug, r.name]
                .filter(Boolean)
                .some((v: any) => String(v).toLowerCase().includes(term))
        );
    }

    closeRoleDetail() {
        this.md.back(this.md.list(this.basePath));
    }

    toggleNewRoleModal() {
        this.selectedRole = { isNew: true, menus: [] };
        this.mode.set(FormMode.CREATE);
        this.roleForm.reset({ isActive: true, level: 2, category: null });
        this.open(this.newRole);
    }

    toggleMenuModal() {
        this.open(this.menuModal);
    }

    handleFormChange(formValue: any) {
        if (formValue === null) return this._resetStateAndReload();
        const value = formValue.value !== undefined ? formValue.value : formValue;
        const data = { ...value };
        if (data.name != null) {
            data.name = String(data.name).trim().toUpperCase().replace(/\s+/g, '_').replace(/[^A-Z0-9_]/g, '');
            data.slug = data.name;
        }
        this.mode() === FormMode.EDIT ? this.updateRole(data) : this.createRole(data);
    }

    private _resetStateAndReload() {
        this.loadedRoleKey = undefined;
        this.loadRoles();
        this.close();
        this.md.go(this.md.list(this.basePath));
    }

    private updateRole(data: any) {
        const key = this.selectedRole?.slug ?? this.selectedRole?.name ?? this.selectedRole?.id;
        if (!key) return;
        this.roleService.update(key, data).subscribe({
            next: (res: any) => {
                this.showToast('Rol actualizado correctamente');
                this.loadRoles();
                this.onRoleSelected(res);
            },
            error: () => this.showToast('No se pudo actualizar el rol', 'error')
        });
    }

    private createRole(data: any) {
        this.roleService.create(data).subscribe({
            next: (res: any) => {
                this.showToast('Rol creado correctamente');
                this.loadRoles();
                this.close();
                this.onRoleSelected(res);
            },
            error: () => this.showToast('No se pudo crear el rol', 'error')
        });
    }

    createRoleWithMenus(roleFormComp: any, menusFormComp: any) {
        const form = roleFormComp.roleForm();
        if (!form || !form.valid) {
            form?.markAllAsTouched();
            return;
        }
        const roleData = { ...form.value };
        const selectedMenuIds = this._menuIdsFromForm(menusFormComp);
        this._execCreateRole(roleData, selectedMenuIds);
    }

    private _execCreateRole(roleData: any, menuIds: string[]) {
        if (roleData.name != null) {
            roleData.name = String(roleData.name).trim().toUpperCase().replace(/\s+/g, '_').replace(/[^A-Z0-9_]/g, '');
            roleData.slug = roleData.name;
        }
        this.roleService.create(roleData).subscribe({
            next: (res: any) => this._onRoleCreated(res, menuIds),
            error: () => this.showToast('No se pudo crear el rol', 'error')
        });
    }

    private _onRoleCreated(res: any, menuIds: string[]) {
        this.showToast('Rol creado correctamente');
        this.loadRoles();
        this.close();
        this._handleInitialMenus(res, menuIds);
    }

    private _handleInitialMenus(newRole: any, menuIds: string[]) {
        const key = newRole?.slug ?? newRole?.name ?? newRole?.id;
        if (!menuIds || menuIds.length === 0) return this.onRoleSelected(newRole);
        const menusData = this._expandMenuIds(menuIds).map((id) => this._readOnlyFlags(id));
        this.roleService.saveMenusBulk(key, menusData).subscribe({
            next: (res: any) => this.onRoleSelected(res),
            error: () => this._handleMenuError(newRole)
        });
    }

    private _handleMenuError(newRole: any) {
        this.showToast('Rol creado, pero no se pudieron asociar los menús', 'error');
        this.onRoleSelected(newRole);
    }

    startMenusEdit() {
        const key = this.selectedRole?.slug ?? this.selectedRole?.id;
        if (!key) return;
        this.menusSnapshot = this._cloneMenus(this.selectedRole.menus);
        this.menusEditing.set(true);
    }

    cancelMenusEdit() {
        if (this.selectedRole) {
            this.selectedRole = { ...this.selectedRole, menus: this._cloneMenus(this.menusSnapshot) };
        }
        this._stopMenusEdit();
    }

    saveMenusDraft() {
        const key = this.selectedRole?.slug ?? this.selectedRole?.name ?? this.selectedRole?.id;
        if (!key) return;
        const menusData = this._mapMenusPayload(this.selectedRole.menus);
        this.roleService.saveMenusBulk(key, menusData).subscribe({
            next: (res) => {
                this.showToast('Permisos actualizados');
                this._applyRoleMenusResponse(res);
            },
            error: () => this.showToast('Error al guardar permisos', 'error')
        });
    }

    onPermissionChange(event: { menuId: any, permission: string }) {
        if (!this.menusEditing() || !this.selectedRole) return;
        const menus = (this.selectedRole.menus || []).map((m: any) => this._toggleFlag(m, event));
        this.selectedRole = { ...this.selectedRole, menus };
    }

    onActionsChange(event: { menuId: any, actions: string[] }) {
        if (!this.menusEditing() || !this.selectedRole) return;
        const menus = (this.selectedRole.menus || []).map((m: any) => {
            if (menuKey(m) !== String(event.menuId)) return m;
            const baseRm = m.RoleMenu ?? m.role_menu ?? {};
            const RoleMenu = { ...baseRm, actions: event.actions };
            return { ...m, RoleMenu, role_menu: RoleMenu };
        });
        this.selectedRole = { ...this.selectedRole, menus };
    }

    saveMenusFromModal(menusFormComp: any) {
        const key = this.selectedRole?.slug ?? this.selectedRole?.id;
        if (!key) return;
        const menus = this._menusFromModal(menusFormComp);
        this.selectedRole = { ...this.selectedRole, menus };
        this.close();
        if (this.menusEditing()) return;
        this.saveMenusDraft();
    }

    private _menusFromModal(menusFormComp: any) {
        const selectedMenuIds = this._expandMenuIds(this._menuIdsFromForm(menusFormComp));
        const current = this.selectedRole.menus || [];
        return this._normalizeMenus(selectedMenuIds.map((id: string) => {
            const existing = current.find((m: any) => menuKey(m) === id);
            return existing || this._draftMenu(id);
        }));
    }

    private _menuIdsFromForm(menusFormComp: any): string[] {
        if (typeof menusFormComp?.selectedIds === 'function') {
            return menusFormComp.selectedIds();
        }
        const raw = menusFormComp?.formRole?.get('menus')?.value ?? [];
        return (Array.isArray(raw) ? raw : [])
            .map((m: any) => typeof m === 'object' ? menuKey(m) : String(m))
            .filter(Boolean);
    }

    removeMenu(menuId: any) {
        if (!this.menusEditing() || !this.selectedRole) return;
        const key = String(menuId);
        const menus = (this.selectedRole.menus || []).filter((m: any) => menuKey(m) !== key);
        this.selectedRole = { ...this.selectedRole, menus };
    }

    private _mapMenusPayload(menus: any[]) {
        return (menus || []).map((m: any) => this._flagsFromMenu(m));
    }

    private _readOnlyFlags(id: string) {
        return { uuid: id, menuId: id, canRead: true, canCreate: false, canUpdate: false, canDelete: false, actions: [] };
    }

    private _flagsFromMenu(m: any) {
        const rm = m.RoleMenu || m.role_menu || {};
        return {
            uuid: m.uuid,
            menuId: m.id ?? m.menuId,
            canCreate: !!(rm.canCreate ?? rm.can_create),
            canRead: !!(rm.canRead ?? rm.can_read ?? true),
            canUpdate: !!(rm.canUpdate ?? rm.can_update),
            canDelete: !!(rm.canDelete ?? rm.can_delete),
            actions: this._parseActions(rm.actions)
        };
    }

    private _normalizeMenus(menus: any[]): any[] {
        const catalogByKey = new Map(this.menuOptions().map((m) => [menuKey(m), m]));
        const merged = (menus || []).map((m: any) => ({
            ...mergeMenuCatalog(m, catalogByKey.get(menuKey(m))),
            RoleMenu: this._roleMenuFrom(m)
        }));
        return flattenMenusAsTree(merged);
    }

    private _expandMenuIds(ids: string[]): string[] {
        return expandMenuIdsWithParents(ids, this.menuOptions());
    }

    private _roleMenuFrom(m: any) {
        const rm = m.RoleMenu || m.role_menu || {};
        return {
            canCreate: !!(rm.canCreate ?? rm.can_create),
            canRead: !!(rm.canRead ?? rm.can_read ?? true),
            canUpdate: !!(rm.canUpdate ?? rm.can_update),
            canDelete: !!(rm.canDelete ?? rm.can_delete),
            actions: this._parseActions(rm.actions)
        };
    }

    private _parseActions(raw: any): string[] {
        if (Array.isArray(raw)) return raw;
        if (typeof raw === 'string' && raw.trim()) {
            try { return JSON.parse(raw); } catch { return []; }
        }
        return [];
    }

    private _toggleFlag(menu: any, event: { menuId: any, permission: string }) {
        if (menuKey(menu) !== String(event.menuId)) return menu;
        const RoleMenu = { ...(menu.RoleMenu || {}) };
        RoleMenu[event.permission] = !RoleMenu[event.permission];
        return { ...menu, RoleMenu };
    }

    private _draftMenu(id: string) {
        const catalog = this.menuOptions().find((m) => menuKey(m) === id) || { uuid: id };
        return { ...catalog, RoleMenu: this._roleMenuFrom(this._readOnlyFlags(id)) };
    }

    private _cloneMenus(menus: any[]): any[] {
        return this._normalizeMenus(JSON.parse(JSON.stringify(menus || [])));
    }

    private _stopMenusEdit() {
        this.menusEditing.set(false);
        this.menusSnapshot = [];
    }

    private _applyRoleMenusResponse(res: any) {
        const menus = this._normalizeMenus(res?.menus);
        this.selectedRole = { ...(res || this.selectedRole), menus };
        this.mode.set(FormMode.VIEW);
        this._stopMenusEdit();
        this.roleForm.patchValue({
            id: this.selectedRole.id,
            slug: this.selectedRole.slug,
            name: this.selectedRole.slug ?? this.selectedRole.name,
            displayName: this.selectedRole.displayName,
            description: this.selectedRole.description,
            level: this.selectedRole.level,
            category: this.selectedRole.category || null,
            isActive: this.selectedRole.isActive
        });
    }

    private showToast(title: string, icon: 'success' | 'error' = 'success') {
        Swal.fire({
            toast: true,
            position: 'top-end',
            showConfirmButton: false,
            timer: 3000,
            timerProgressBar: true,
            icon: icon,
            title: title
        });
    }

    getIds(items: any[]): string[] {
        return (items || []).map((i) => menuKey(i)).filter(Boolean);
    }

    menuOptions = computed(() => {
        const catalog = this.catalogService.menus() || [];
        return flattenMenusAsTree(catalog);
    });

    compareRoles = (a: any, b: any) =>
        (a?.slug ?? a?.name ?? a?.id) === (b?.slug ?? b?.name ?? b?.id);
}
